import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Waves, Shapes, Play, Square, Volume2 } from 'lucide-react';
import SacredGeometryCanvas from './SacredGeometryCanvas';
import toast from 'react-hot-toast';
import { audioManager } from '../core/audio/AudioManagerAdapter';
import { getCymaticFrequency, getFrequencyDescription, getPrimaryFrequency, getAllOscillators } from '../utils/cymaticFrequencies';

/**
 * Visual Harmony Studio - Cymatic Generator
 * 
 * Real-time cymatic pattern visualization driven by complex multi-frequency audio synthesis.
 * Each sacred geometry pattern is generated through the interaction of multiple oscillators,
 * FM synthesis, and LFO modulation creating authentic cymatic interference patterns.
 */
interface VisualHarmonyStudioProps {
  onPatternChange?: (pattern: string) => void;
  onAudioDataChange?: (audioData: { frequencyData: Uint8Array; averageAmplitude: number } | null, isPlaying: boolean) => void;
}

type PatternOption = {
  id: string;
  name: string;
  category: 'Sacred' | 'Geometric' | 'Chladni' | 'Solfeggio' | 'Natural';
};

type PatternTheme = {
  glyph: string;
  archetype: string;
  shape: string;
  activeGradient: string;
  inactiveGradient: string;
  borderColor: string;
  activeText: string;
  inactiveText: string;
  borderStyle: 'solid' | 'dashed' | 'dotted' | 'double';
  motif: string;
};

const PATTERN_OPTIONS: PatternOption[] = [
  { id: 'flower-of-life', name: 'Flower of Life', category: 'Sacred' },
  { id: 'seed-of-life', name: 'Seed of Life', category: 'Sacred' },
  { id: 'fruit-of-life', name: 'Fruit of Life', category: 'Sacred' },
  { id: 'vesica-piscis', name: 'Vesica Piscis', category: 'Sacred' },
  { id: 'metatrons-cube', name: "Metatron's Cube", category: 'Sacred' },
  { id: 'sri-yantra', name: 'Sri Yantra', category: 'Sacred' },
  { id: 'tree-of-life', name: 'Tree of Life', category: 'Sacred' },
  { id: 'merkaba', name: 'Merkaba', category: 'Sacred' },
  { id: 'hexagon', name: 'Hexagon', category: 'Geometric' },
  { id: 'pentagon', name: 'Pentagon', category: 'Geometric' },
  { id: 'octagon', name: 'Octagon', category: 'Geometric' },
  { id: 'dodecagon', name: 'Dodecagon', category: 'Geometric' },
  { id: 'star-pentagram', name: 'Star Pentagram', category: 'Geometric' },
  { id: 'mandala', name: 'Mandala', category: 'Geometric' },
  { id: 'chladni-1-1', name: 'Chladni (1,1)', category: 'Chladni' },
  { id: 'chladni-2-2', name: 'Chladni (2,2)', category: 'Chladni' },
  { id: 'chladni-3-3', name: 'Chladni (3,3)', category: 'Chladni' },
  { id: 'chladni-hexagon', name: 'Chladni Hexagon', category: 'Chladni' },
  { id: 'chladni-radial', name: 'Chladni Radial', category: 'Chladni' },
  { id: 'solfeggio-174', name: '174 Hz - Foundation', category: 'Solfeggio' },
  { id: 'solfeggio-285', name: '285 Hz - Grounding', category: 'Solfeggio' },
  { id: 'solfeggio-417', name: '417 Hz - Change', category: 'Solfeggio' },
  { id: 'solfeggio-528', name: '528 Hz - Heart', category: 'Solfeggio' },
  { id: 'solfeggio-639', name: '639 Hz - Connection', category: 'Solfeggio' },
  { id: 'solfeggio-741', name: '741 Hz - Voice', category: 'Solfeggio' },
  { id: 'solfeggio-852', name: '852 Hz - Insight', category: 'Solfeggio' },
  { id: 'solfeggio-963', name: '963 Hz - Crown', category: 'Solfeggio' },
  { id: 'golden-spiral', name: 'Golden Spiral', category: 'Natural' },
  { id: 'spiral-galaxy', name: 'Spiral Galaxy', category: 'Natural' },
  { id: 'torus', name: 'Torus', category: 'Natural' },
  { id: 'snowflake', name: 'Snowflake', category: 'Natural' },
  { id: 'water-cymatics', name: 'Water Cymatics', category: 'Natural' },
  { id: 'yin-yang', name: 'Yin-Yang', category: 'Natural' }
];

const PATTERN_THEMES: Record<string, PatternTheme> = {
  'flower-of-life': { glyph: '✺', archetype: 'Unity Matrix', shape: '24px 8px 24px 8px', activeGradient: 'linear-gradient(135deg, #14532d 0%, #4d7c0f 45%, #ca8a04 100%)', inactiveGradient: 'linear-gradient(135deg, rgba(20,83,45,0.45), rgba(202,138,4,0.2))', borderColor: '#84cc16', activeText: '#ecfccb', inactiveText: '#d9f99d', borderStyle: 'double', motif: 'radial-gradient(circle at 20% 20%, rgba(236,252,203,0.25), transparent 45%)' },
  'seed-of-life': { glyph: '◉', archetype: 'Origin Pulse', shape: '40px 40px 12px 12px', activeGradient: 'linear-gradient(140deg, #78350f 0%, #b45309 55%, #fde68a 100%)', inactiveGradient: 'linear-gradient(140deg, rgba(120,53,15,0.5), rgba(245,158,11,0.18))', borderColor: '#f59e0b', activeText: '#fffbeb', inactiveText: '#fde68a', borderStyle: 'solid', motif: 'radial-gradient(circle at 80% 25%, rgba(255,251,235,0.22), transparent 48%)' },
  'fruit-of-life': { glyph: '⬢', archetype: 'Life Blueprint', shape: '14px 30px 14px 30px', activeGradient: 'linear-gradient(135deg, #134e4a 0%, #0e7490 52%, #67e8f9 100%)', inactiveGradient: 'linear-gradient(135deg, rgba(19,78,74,0.52), rgba(14,116,144,0.22))', borderColor: '#22d3ee', activeText: '#ecfeff', inactiveText: '#a5f3fc', borderStyle: 'double', motif: 'radial-gradient(circle at 18% 72%, rgba(236,254,255,0.18), transparent 45%)' },
  'vesica-piscis': { glyph: '◌', archetype: 'Sacred Gateway', shape: '56px 56px 8px 8px', activeGradient: 'linear-gradient(145deg, #312e81 0%, #3730a3 50%, #a5b4fc 100%)', inactiveGradient: 'linear-gradient(145deg, rgba(49,46,129,0.52), rgba(99,102,241,0.22))', borderColor: '#818cf8', activeText: '#eef2ff', inactiveText: '#c7d2fe', borderStyle: 'solid', motif: 'radial-gradient(circle at 50% 25%, rgba(238,242,255,0.25), transparent 55%)' },
  'metatrons-cube': { glyph: '✷', archetype: 'Cosmic Lattice', shape: '6px 22px 6px 22px', activeGradient: 'linear-gradient(135deg, #0c4a6e 0%, #0369a1 55%, #67e8f9 100%)', inactiveGradient: 'linear-gradient(135deg, rgba(12,74,110,0.55), rgba(3,105,161,0.2))', borderColor: '#38bdf8', activeText: '#f0f9ff', inactiveText: '#bae6fd', borderStyle: 'double', motif: 'radial-gradient(circle at 80% 80%, rgba(240,249,255,0.2), transparent 45%)' },
  'sri-yantra': { glyph: '△', archetype: 'Shakti Balance', shape: '28px 4px 28px 4px', activeGradient: 'linear-gradient(130deg, #7f1d1d 0%, #be123c 48%, #fda4af 100%)', inactiveGradient: 'linear-gradient(130deg, rgba(127,29,29,0.58), rgba(190,18,60,0.2))', borderColor: '#fb7185', activeText: '#fff1f2', inactiveText: '#fecdd3', borderStyle: 'solid', motif: 'radial-gradient(circle at 16% 22%, rgba(255,241,242,0.2), transparent 45%)' },
  'tree-of-life': { glyph: '♁', archetype: 'Pillar Ascent', shape: '12px 38px 12px 38px', activeGradient: 'linear-gradient(135deg, #14532d 0%, #166534 48%, #86efac 100%)', inactiveGradient: 'linear-gradient(135deg, rgba(20,83,45,0.6), rgba(22,101,52,0.2))', borderColor: '#4ade80', activeText: '#f0fdf4', inactiveText: '#bbf7d0', borderStyle: 'double', motif: 'radial-gradient(circle at 50% 78%, rgba(240,253,244,0.2), transparent 48%)' },
  'merkaba': { glyph: '✶', archetype: 'Light Vehicle', shape: '4px 30px 30px 4px', activeGradient: 'linear-gradient(135deg, #1e1b4b 0%, #3730a3 45%, #22d3ee 100%)', inactiveGradient: 'linear-gradient(135deg, rgba(30,27,75,0.65), rgba(34,211,238,0.18))', borderColor: '#67e8f9', activeText: '#ecfeff', inactiveText: '#a5f3fc', borderStyle: 'solid', motif: 'radial-gradient(circle at 75% 28%, rgba(236,254,255,0.2), transparent 48%)' },
  'hexagon': { glyph: '⬡', archetype: 'Harmonic Cell', shape: '14px 28px 14px 28px', activeGradient: 'linear-gradient(135deg, #78350f 0%, #a16207 55%, #facc15 100%)', inactiveGradient: 'linear-gradient(135deg, rgba(120,53,15,0.58), rgba(250,204,21,0.18))', borderColor: '#facc15', activeText: '#fefce8', inactiveText: '#fde68a', borderStyle: 'solid', motif: 'radial-gradient(circle at 25% 75%, rgba(254,252,232,0.15), transparent 45%)' },
  'pentagon': { glyph: '⬟', archetype: 'Will Form', shape: '26px 10px 26px 10px', activeGradient: 'linear-gradient(135deg, #7f1d1d 0%, #dc2626 52%, #fca5a5 100%)', inactiveGradient: 'linear-gradient(135deg, rgba(127,29,29,0.55), rgba(220,38,38,0.2))', borderColor: '#f87171', activeText: '#fff1f2', inactiveText: '#fecaca', borderStyle: 'solid', motif: 'radial-gradient(circle at 20% 20%, rgba(255,241,242,0.16), transparent 45%)' },
  'octagon': { glyph: '⯃', archetype: 'Order Gate', shape: '10px 10px 30px 30px', activeGradient: 'linear-gradient(135deg, #0f766e 0%, #0d9488 55%, #5eead4 100%)', inactiveGradient: 'linear-gradient(135deg, rgba(15,118,110,0.55), rgba(13,148,136,0.2))', borderColor: '#2dd4bf', activeText: '#f0fdfa', inactiveText: '#99f6e4', borderStyle: 'dashed', motif: 'radial-gradient(circle at 78% 22%, rgba(240,253,250,0.16), transparent 50%)' },
  'dodecagon': { glyph: '⎔', archetype: 'Cycle Wheel', shape: '18px 18px 34px 6px', activeGradient: 'linear-gradient(135deg, #172554 0%, #1d4ed8 52%, #93c5fd 100%)', inactiveGradient: 'linear-gradient(135deg, rgba(23,37,84,0.6), rgba(29,78,216,0.2))', borderColor: '#60a5fa', activeText: '#eff6ff', inactiveText: '#bfdbfe', borderStyle: 'dotted', motif: 'radial-gradient(circle at 50% 14%, rgba(239,246,255,0.2), transparent 42%)' },
  'star-pentagram': { glyph: '✦', archetype: 'Protective Star', shape: '36px 6px 36px 6px', activeGradient: 'linear-gradient(135deg, #450a0a 0%, #b91c1c 50%, #fda4af 100%)', inactiveGradient: 'linear-gradient(135deg, rgba(69,10,10,0.65), rgba(185,28,28,0.22))', borderColor: '#f43f5e', activeText: '#fff1f2', inactiveText: '#fecdd3', borderStyle: 'double', motif: 'radial-gradient(circle at 82% 18%, rgba(255,241,242,0.2), transparent 45%)' },
  'mandala': { glyph: '✸', archetype: 'Inner Cosmos', shape: '50% 16% 50% 16%', activeGradient: 'linear-gradient(135deg, #78350f 0%, #ea580c 52%, #fcd34d 100%)', inactiveGradient: 'linear-gradient(135deg, rgba(120,53,15,0.6), rgba(234,88,12,0.2))', borderColor: '#fb923c', activeText: '#fffbeb', inactiveText: '#fed7aa', borderStyle: 'double', motif: 'radial-gradient(circle at 48% 48%, rgba(255,251,235,0.2), transparent 46%)' },
  'chladni-1-1': { glyph: '∿', archetype: 'Fundamental Mode', shape: '8px 8px 24px 24px', activeGradient: 'linear-gradient(135deg, #1f2937 0%, #374151 55%, #9ca3af 100%)', inactiveGradient: 'linear-gradient(135deg, rgba(31,41,55,0.65), rgba(156,163,175,0.2))', borderColor: '#9ca3af', activeText: '#f9fafb', inactiveText: '#d1d5db', borderStyle: 'solid', motif: 'radial-gradient(circle at 20% 80%, rgba(249,250,251,0.12), transparent 42%)' },
  'chladni-2-2': { glyph: '⌗', archetype: 'Quadrant Nodes', shape: '8px 24px 8px 24px', activeGradient: 'linear-gradient(135deg, #111827 0%, #334155 55%, #94a3b8 100%)', inactiveGradient: 'linear-gradient(135deg, rgba(17,24,39,0.67), rgba(148,163,184,0.2))', borderColor: '#94a3b8', activeText: '#f8fafc', inactiveText: '#cbd5e1', borderStyle: 'dashed', motif: 'radial-gradient(circle at 75% 26%, rgba(248,250,252,0.12), transparent 44%)' },
  'chladni-3-3': { glyph: '⋮', archetype: 'Harmonic Grid', shape: '6px 26px 26px 6px', activeGradient: 'linear-gradient(135deg, #0f172a 0%, #1e293b 55%, #64748b 100%)', inactiveGradient: 'linear-gradient(135deg, rgba(15,23,42,0.7), rgba(100,116,139,0.2))', borderColor: '#64748b', activeText: '#f1f5f9', inactiveText: '#cbd5e1', borderStyle: 'dotted', motif: 'radial-gradient(circle at 50% 20%, rgba(241,245,249,0.12), transparent 46%)' },
  'chladni-hexagon': { glyph: '⌬', archetype: 'Hex Resonance', shape: '16px 34px 16px 4px', activeGradient: 'linear-gradient(135deg, #334155 0%, #475569 55%, #cbd5e1 100%)', inactiveGradient: 'linear-gradient(135deg, rgba(51,65,85,0.7), rgba(203,213,225,0.18))', borderColor: '#cbd5e1', activeText: '#f8fafc', inactiveText: '#e2e8f0', borderStyle: 'solid', motif: 'radial-gradient(circle at 82% 78%, rgba(248,250,252,0.12), transparent 42%)' },
  'chladni-radial': { glyph: '◔', archetype: 'Radial Node Rings', shape: '32px 12px 32px 12px', activeGradient: 'linear-gradient(135deg, #374151 0%, #4b5563 55%, #d1d5db 100%)', inactiveGradient: 'linear-gradient(135deg, rgba(55,65,81,0.66), rgba(209,213,219,0.18))', borderColor: '#d1d5db', activeText: '#f9fafb', inactiveText: '#e5e7eb', borderStyle: 'double', motif: 'radial-gradient(circle at 50% 50%, rgba(249,250,251,0.15), transparent 52%)' },
  'solfeggio-174': { glyph: '♭', archetype: 'Grounded Root', shape: '10px 28px 10px 28px', activeGradient: 'linear-gradient(135deg, #78350f 0%, #92400e 55%, #fb923c 100%)', inactiveGradient: 'linear-gradient(135deg, rgba(120,53,15,0.68), rgba(251,146,60,0.2))', borderColor: '#fb923c', activeText: '#fff7ed', inactiveText: '#fed7aa', borderStyle: 'solid', motif: 'radial-gradient(circle at 24% 75%, rgba(255,247,237,0.12), transparent 44%)' },
  'solfeggio-285': { glyph: '♮', archetype: 'Restorative Soil', shape: '30px 8px 30px 8px', activeGradient: 'linear-gradient(135deg, #14532d 0%, #3f6212 52%, #84cc16 100%)', inactiveGradient: 'linear-gradient(135deg, rgba(20,83,45,0.68), rgba(132,204,22,0.2))', borderColor: '#84cc16', activeText: '#f7fee7', inactiveText: '#d9f99d', borderStyle: 'dashed', motif: 'radial-gradient(circle at 68% 72%, rgba(247,254,231,0.12), transparent 44%)' },
  'solfeggio-417': { glyph: '↻', archetype: 'Pattern Shift', shape: '14px 22px 32px 8px', activeGradient: 'linear-gradient(135deg, #7c2d12 0%, #ea580c 52%, #fdba74 100%)', inactiveGradient: 'linear-gradient(135deg, rgba(124,45,18,0.68), rgba(253,186,116,0.2))', borderColor: '#fdba74', activeText: '#fff7ed', inactiveText: '#fed7aa', borderStyle: 'solid', motif: 'radial-gradient(circle at 82% 28%, rgba(255,247,237,0.14), transparent 44%)' },
  'solfeggio-528': { glyph: '♥', archetype: 'Heart Coherence', shape: '26px 26px 10px 10px', activeGradient: 'linear-gradient(135deg, #7f1d1d 0%, #be123c 50%, #fda4af 100%)', inactiveGradient: 'linear-gradient(135deg, rgba(127,29,29,0.72), rgba(253,164,175,0.2))', borderColor: '#fb7185', activeText: '#fff1f2', inactiveText: '#fecdd3', borderStyle: 'double', motif: 'radial-gradient(circle at 50% 20%, rgba(255,241,242,0.16), transparent 45%)' },
  'solfeggio-639': { glyph: '∞', archetype: 'Relational Bridge', shape: '18px 34px 18px 34px', activeGradient: 'linear-gradient(135deg, #164e63 0%, #0e7490 52%, #67e8f9 100%)', inactiveGradient: 'linear-gradient(135deg, rgba(22,78,99,0.68), rgba(103,232,249,0.2))', borderColor: '#22d3ee', activeText: '#ecfeff', inactiveText: '#a5f3fc', borderStyle: 'solid', motif: 'radial-gradient(circle at 22% 22%, rgba(236,254,255,0.14), transparent 42%)' },
  'solfeggio-741': { glyph: '◉', archetype: 'Clear Voice', shape: '6px 34px 20px 20px', activeGradient: 'linear-gradient(135deg, #172554 0%, #1d4ed8 52%, #93c5fd 100%)', inactiveGradient: 'linear-gradient(135deg, rgba(23,37,84,0.7), rgba(147,197,253,0.2))', borderColor: '#60a5fa', activeText: '#eff6ff', inactiveText: '#bfdbfe', borderStyle: 'dotted', motif: 'radial-gradient(circle at 76% 18%, rgba(239,246,255,0.14), transparent 42%)' },
  'solfeggio-852': { glyph: '◈', archetype: 'Inner Sight', shape: '34px 12px 34px 12px', activeGradient: 'linear-gradient(135deg, #312e81 0%, #4338ca 50%, #a5b4fc 100%)', inactiveGradient: 'linear-gradient(135deg, rgba(49,46,129,0.72), rgba(165,180,252,0.2))', borderColor: '#818cf8', activeText: '#eef2ff', inactiveText: '#c7d2fe', borderStyle: 'double', motif: 'radial-gradient(circle at 48% 50%, rgba(238,242,255,0.18), transparent 48%)' },
  'solfeggio-963': { glyph: '✹', archetype: 'Crown Radiance', shape: '50% 50% 14px 14px', activeGradient: 'linear-gradient(135deg, #4c1d95 0%, #7e22ce 50%, #d8b4fe 100%)', inactiveGradient: 'linear-gradient(135deg, rgba(76,29,149,0.72), rgba(216,180,254,0.2))', borderColor: '#c084fc', activeText: '#faf5ff', inactiveText: '#e9d5ff', borderStyle: 'double', motif: 'radial-gradient(circle at 50% 15%, rgba(250,245,255,0.16), transparent 46%)' },
  'golden-spiral': { glyph: 'φ', archetype: 'Proportion Flow', shape: '8px 26px 34px 14px', activeGradient: 'linear-gradient(135deg, #854d0e 0%, #ca8a04 52%, #fde047 100%)', inactiveGradient: 'linear-gradient(135deg, rgba(133,77,14,0.72), rgba(253,224,71,0.2))', borderColor: '#facc15', activeText: '#fefce8', inactiveText: '#fde68a', borderStyle: 'solid', motif: 'radial-gradient(circle at 82% 82%, rgba(254,252,232,0.16), transparent 46%)' },
  'spiral-galaxy': { glyph: '🜄', archetype: 'Galactic Turn', shape: '34px 14px 24px 4px', activeGradient: 'linear-gradient(135deg, #1e1b4b 0%, #3730a3 48%, #60a5fa 100%)', inactiveGradient: 'linear-gradient(135deg, rgba(30,27,75,0.74), rgba(96,165,250,0.2))', borderColor: '#93c5fd', activeText: '#eff6ff', inactiveText: '#bfdbfe', borderStyle: 'dashed', motif: 'radial-gradient(circle at 14% 20%, rgba(239,246,255,0.16), transparent 48%)' },
  'torus': { glyph: '⊙', archetype: 'Field Continuum', shape: '16px 38px 38px 16px', activeGradient: 'linear-gradient(135deg, #0f766e 0%, #0891b2 50%, #67e8f9 100%)', inactiveGradient: 'linear-gradient(135deg, rgba(15,118,110,0.74), rgba(103,232,249,0.2))', borderColor: '#22d3ee', activeText: '#f0fdfa', inactiveText: '#a5f3fc', borderStyle: 'double', motif: 'radial-gradient(circle at 52% 50%, rgba(240,253,250,0.16), transparent 52%)' },
  'snowflake': { glyph: '❄', archetype: 'Crystalline Symmetry', shape: '12px 36px 12px 36px', activeGradient: 'linear-gradient(135deg, #0c4a6e 0%, #0284c7 48%, #bae6fd 100%)', inactiveGradient: 'linear-gradient(135deg, rgba(12,74,110,0.74), rgba(186,230,253,0.2))', borderColor: '#7dd3fc', activeText: '#f0f9ff', inactiveText: '#bae6fd', borderStyle: 'dotted', motif: 'radial-gradient(circle at 22% 78%, rgba(240,249,255,0.16), transparent 46%)' },
  'water-cymatics': { glyph: '≈', archetype: 'Fluid Resonance', shape: '28px 28px 8px 22px', activeGradient: 'linear-gradient(135deg, #0f172a 0%, #0369a1 50%, #7dd3fc 100%)', inactiveGradient: 'linear-gradient(135deg, rgba(15,23,42,0.76), rgba(125,211,252,0.2))', borderColor: '#38bdf8', activeText: '#f0f9ff', inactiveText: '#bae6fd', borderStyle: 'solid', motif: 'radial-gradient(circle at 74% 74%, rgba(240,249,255,0.14), transparent 46%)' },
  'yin-yang': { glyph: '☯', archetype: 'Dynamic Balance', shape: '50% 14% 50% 14%', activeGradient: 'linear-gradient(135deg, #020617 0%, #27272a 50%, #fafafa 100%)', inactiveGradient: 'linear-gradient(135deg, rgba(2,6,23,0.82), rgba(250,250,250,0.14))', borderColor: '#e4e4e7', activeText: '#fafafa', inactiveText: '#e4e4e7', borderStyle: 'double', motif: 'radial-gradient(circle at 50% 50%, rgba(250,250,250,0.12), transparent 56%)' }
};

const getPatternButtonStyle = (patternId: string, active: boolean, isPlayingPattern: boolean): React.CSSProperties => {
  const theme = PATTERN_THEMES[patternId];
  if (!theme) {
    return {
      padding: '8px',
      borderRadius: '8px',
      background: active ? 'rgba(139, 92, 246, 0.2)' : 'rgba(15, 23, 42, 0.6)',
      border: active ? '2px solid #8b5cf6' : '2px solid rgba(100, 116, 139, 0.3)',
      color: active ? '#8b5cf6' : '#64748b',
      fontSize: '0.7rem',
      fontWeight: 600,
      cursor: 'pointer',
      textAlign: 'center',
      position: 'relative',
      overflow: 'visible'
    };
  }

  return {
    padding: '0.5rem 0.42rem',
    minHeight: '74px',
    borderRadius: theme.shape,
    background: active ? `${theme.motif}, ${theme.activeGradient}` : `${theme.motif}, ${theme.inactiveGradient}`,
    border: `2px ${theme.borderStyle} ${theme.borderColor}`,
    color: active ? theme.activeText : theme.inactiveText,
    fontSize: '0.66rem',
    fontWeight: 700,
    cursor: 'pointer',
    textAlign: 'center',
    letterSpacing: '0.02em',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    overflow: 'visible',
    isolation: 'isolate',
    gap: '0.15rem',
    boxShadow: isPlayingPattern
      ? `0 0 0 1px ${theme.borderColor}aa, 0 0 20px ${theme.borderColor}99, 0 14px 24px rgba(2, 6, 23, 0.45)`
      : active
      ? `0 0 0 1px ${theme.borderColor}55, 0 10px 18px rgba(2, 6, 23, 0.35)`
      : `0 0 0 1px ${theme.borderColor}22, inset 0 1px 0 rgba(255, 255, 255, 0.06)`
  };
};

const VisualHarmonyStudio: React.FC<VisualHarmonyStudioProps> = ({ onPatternChange, onAudioDataChange }) => {
  const [geometryPattern, setGeometryPattern] = useState('flower-of-life');
  const [isPlayingCymatic, setIsPlayingCymatic] = useState(false);
  const [volume, setVolume] = useState(1);
  const [audioData, setAudioData] = useState<{ frequencyData: Uint8Array; averageAmplitude: number } | null>(null);
  
  // Refs for audio oscillators
  const oscillatorsRef = useRef<OscillatorNode[]>([]);
  const gainNodesRef = useRef<GainNode[]>([]);
  const lfoOscillatorsRef = useRef<OscillatorNode[]>([]);
  const lfoGainNodesRef = useRef<GainNode[]>([]);
  const fmModulatorsRef = useRef<OscillatorNode[]>([]);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const cymaticMasterGainRef = useRef<GainNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const isTransitioningRef = useRef<boolean>(false);



  /**
   * Start real-time audio analysis for visualization
   */
  const startAudioAnalysis = () => {
    if (!analyserRef.current) return;

    const analyser = analyserRef.current;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const analyze = () => {
      // Check if still playing and analyser exists
      if (!analyserRef.current) {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        return;
      }

      analyser.getByteFrequencyData(dataArray);

      // Calculate average amplitude for overall visualization intensity
      let sum = 0;
      for (let i = 0; i < bufferLength; i++) {
        sum += dataArray[i];
      }
      const averageAmplitude = sum / bufferLength / 255; // Normalize to 0-1

      const newAudioData = {
        frequencyData: dataArray,
        averageAmplitude
      };
      setAudioData(newAudioData);
      if (onAudioDataChange) {
        onAudioDataChange(newAudioData, isPlayingCymatic);
      }

      animationFrameRef.current = requestAnimationFrame(analyze);
    };

    analyze();
  };

  // Notify when audio stops
  useEffect(() => {
    if (!isPlayingCymatic && onAudioDataChange) {
      onAudioDataChange(null, false);
    }
  }, [isPlayingCymatic, onAudioDataChange]);

  /**
   * Stop all cymatic oscillators with optional fade out
   */
  const stopCymaticSound = (fadeOut: boolean = false, fadeDuration: number = 0.5) => {
    const audioContext = audioManager.getMainAudioContext();
    if (!audioContext) {
      // If no audio context, do immediate stop
      fadeOut = false;
    }

    if (fadeOut && audioContext && (cymaticMasterGainRef.current || gainNodesRef.current.length > 0)) {
      // Fade out all gain nodes smoothly
      const currentTime = audioContext.currentTime;
      const fadeEndTime = currentTime + fadeDuration;

      const nodesToFade = cymaticMasterGainRef.current
        ? [cymaticMasterGainRef.current]
        : gainNodesRef.current;

      nodesToFade.forEach((gain) => {
        try {
          const currentGain = gain.gain.value;
          gain.gain.cancelScheduledValues(currentTime);
          gain.gain.setValueAtTime(currentGain, currentTime);
          gain.gain.linearRampToValueAtTime(0, fadeEndTime);
        } catch (e) {
          // Gain node may be disconnected, continue
        }
      });

      // Also fade out FM gain nodes if they exist
      // Note: FM gain nodes are stored in gainNodesRef for FM synthesis
      
      // Schedule cleanup after fade completes
      setTimeout(() => {
        // Stop animation loop
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }

        // Stop all main oscillators
        oscillatorsRef.current.forEach((osc) => {
          try {
            osc.stop();
            osc.disconnect();
          } catch (e) {
            // Already stopped
          }
        });
        
        // Stop LFO oscillators
        lfoOscillatorsRef.current.forEach((lfo) => {
          try {
            lfo.stop();
            lfo.disconnect();
          } catch (e) {
            // Already stopped
          }
        });
        
        // Stop FM modulator oscillators
        fmModulatorsRef.current.forEach((mod) => {
          try {
            mod.stop();
            mod.disconnect();
          } catch (e) {
            // Already stopped
          }
        });
        
        // Disconnect all gain nodes
        gainNodesRef.current.forEach((gain) => {
          try {
            gain.disconnect();
          } catch (e) {
            // Already disconnected
          }
        });
        
        lfoGainNodesRef.current.forEach((gain) => {
          try {
            gain.disconnect();
          } catch (e) {
            // Already disconnected
          }
        });
        
        if (analyserRef.current) {
          try {
            analyserRef.current.disconnect();
          } catch (e) {
            // Already disconnected
          }
        }

        if (cymaticMasterGainRef.current) {
          try {
            cymaticMasterGainRef.current.disconnect();
          } catch (e) {
            // Already disconnected
          }
        }

        // Clear all refs
        oscillatorsRef.current = [];
        gainNodesRef.current = [];
        lfoOscillatorsRef.current = [];
        lfoGainNodesRef.current = [];
        fmModulatorsRef.current = [];
        analyserRef.current = null;
        cymaticMasterGainRef.current = null;
        setAudioData(null);
        setIsPlayingCymatic(false);
        // Don't reset isTransitioningRef here - it will be reset when new sound starts
      }, fadeDuration * 1000 + 50); // Add small buffer
    } else {
      // Immediate stop (no fade)
    // Stop animation loop
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Stop all main oscillators
    oscillatorsRef.current.forEach((osc) => {
      try {
        osc.stop();
        osc.disconnect();
      } catch (e) {
        // Already stopped
      }
    });
    
    // Stop LFO oscillators
    lfoOscillatorsRef.current.forEach((lfo) => {
      try {
        lfo.stop();
        lfo.disconnect();
      } catch (e) {
        // Already stopped
      }
    });
    
    // Stop FM modulator oscillators
    fmModulatorsRef.current.forEach((mod) => {
      try {
        mod.stop();
        mod.disconnect();
      } catch (e) {
        // Already stopped
      }
    });
    
    // Disconnect all gain nodes
    gainNodesRef.current.forEach((gain) => {
      try {
        gain.disconnect();
      } catch (e) {
        // Already disconnected
      }
    });
    
    lfoGainNodesRef.current.forEach((gain) => {
      try {
        gain.disconnect();
      } catch (e) {
        // Already disconnected
      }
    });
    
    if (analyserRef.current) {
      try {
        analyserRef.current.disconnect();
      } catch (e) {
        // Already disconnected
      }
    }

    if (cymaticMasterGainRef.current) {
      try {
        cymaticMasterGainRef.current.disconnect();
      } catch (e) {
        // Already disconnected
      }
    }

    // Clear all refs
    oscillatorsRef.current = [];
    gainNodesRef.current = [];
    lfoOscillatorsRef.current = [];
    lfoGainNodesRef.current = [];
    fmModulatorsRef.current = [];
    analyserRef.current = null;
    cymaticMasterGainRef.current = null;
    setAudioData(null);
    setIsPlayingCymatic(false);
      isTransitioningRef.current = false;
    }
  };

  /**
   * Play cymatic sound for the current pattern
   */
  const playCymaticSound = async (fadeIn: boolean = false, fadeDuration: number = 0.5, patternOverride?: string) => {
    // Stop any existing sound (skip if we're transitioning - fade out already handled)
    if (!fadeIn || !isTransitioningRef.current) {
      stopCymaticSound();
    }

    // Use pattern override if provided, otherwise use current state
    const patternToUse = patternOverride || geometryPattern;

    // Get frequency config for current pattern
    const frequencyConfig = getCymaticFrequency(patternToUse);
    if (!frequencyConfig) {
      toast.error('No cymatic frequency mapping for this pattern');
      return;
    }

    // Ensure audio is initialized
    if (!audioManager.isReady()) {
      try {
        await audioManager.initialize();
      } catch (error) {
        toast.error('Audio initialization failed. Please interact with the page first.');
        return;
      }
    }

    const audioContext = audioManager.getMainAudioContext();
    const masterGain = audioManager.getMainMasterGain();

    if (!audioContext || !masterGain) {
      toast.error('Audio context not available');
      return;
    }

    // Resume context if suspended
    if (audioContext.state === 'suspended') {
      await audioContext.resume();
    }

    // Create analyser node for real-time audio analysis
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 2048; // Higher resolution for better frequency analysis
    analyser.smoothingTimeConstant = 0.8; // Smooth transitions
    analyserRef.current = analyser;

    const currentTime = audioContext.currentTime;
    const cymaticMasterGain = audioContext.createGain();
    cymaticMasterGain.gain.setValueAtTime(volume, currentTime);
    cymaticMasterGainRef.current = cymaticMasterGain;

    // Route all cymatic sources through one shared gain so the slider controls everything.
    cymaticMasterGain.connect(analyser);
    cymaticMasterGain.connect(masterGain);

    const oscillators = frequencyConfig.oscillators || [];

    // Create FM synthesis if configured
    if (frequencyConfig.carrierFrequency && frequencyConfig.modulatorFrequency) {
      const carrier = audioContext.createOscillator();
      const modulator = audioContext.createOscillator();
      const carrierGain = audioContext.createGain();
      const modulatorGain = audioContext.createGain();
      const fmGain = audioContext.createGain();

      carrier.type = 'sine';
      carrier.frequency.value = frequencyConfig.carrierFrequency;
      
      modulator.type = 'sine';
      modulator.frequency.value = frequencyConfig.modulatorFrequency;
      
      // FM synthesis: modulator modulates carrier frequency (gentler for meditation)
      modulatorGain.gain.value = (frequencyConfig.modulationIndex || 2.0) * 0.5; // Reduced modulation
      carrier.frequency.setValueAtTime(frequencyConfig.carrierFrequency, currentTime);
      
      // Add low-pass filter to FM for warmer sound
      const fmLowpass = audioContext.createBiquadFilter();
      fmLowpass.type = 'lowpass';
      fmLowpass.frequency.value = 3000; // Soft cutoff
      fmLowpass.Q.value = 1;
      
      // Connect modulator to carrier frequency
      modulator.connect(modulatorGain);
      modulatorGain.connect(carrier.frequency);
      
      carrierGain.gain.value = 0;
      fmGain.gain.value = volume * 0.4; // Reduced volume for calmness
      
      // Slow, wave-like fade in (or smooth transition fade in)
      carrierGain.gain.setValueAtTime(0, currentTime);
      if (fadeIn) {
        // Smooth fade in for pattern transitions
        carrierGain.gain.linearRampToValueAtTime(0.3, currentTime + fadeDuration);
        carrierGain.gain.linearRampToValueAtTime(1, currentTime + fadeDuration + 1.5);
      } else {
        // Original slow fade in
        carrierGain.gain.linearRampToValueAtTime(0.3, currentTime + 2); // Slow 2-second fade
        carrierGain.gain.linearRampToValueAtTime(1, currentTime + 4);
      }
      
      // Add wave-like modulation to FM gain
      const fmWaveLFO = audioContext.createOscillator();
      const fmWaveGain = audioContext.createGain();
      fmWaveLFO.type = 'sine';
      fmWaveLFO.frequency.value = 0.1; // Very slow wave (10 second period)
      fmWaveGain.gain.value = 0.2; // 20% volume variation
      
      fmWaveLFO.connect(fmWaveGain);
      fmWaveGain.connect(fmGain.gain);
      fmWaveLFO.start(currentTime);
      lfoOscillatorsRef.current.push(fmWaveLFO);
      lfoGainNodesRef.current.push(fmWaveGain);
      
      // Connect: carrier -> carrierGain -> fmLowpass -> fmGain -> cymaticMasterGain
      carrier.connect(carrierGain);
      carrierGain.connect(fmLowpass);
      fmLowpass.connect(fmGain);
      fmGain.connect(cymaticMasterGain);
      
      carrier.start(currentTime);
      modulator.start(currentTime);
      
      oscillatorsRef.current.push(carrier);
      gainNodesRef.current.push(fmGain);
      fmModulatorsRef.current.push(modulator);
      lfoGainNodesRef.current.push(modulatorGain);
    }

    // Create multiple independent oscillators for complex patterns with ambient wave-like modulation
    oscillators.forEach((oscConfig, index) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      // Use softer waveforms - triangle is gentler than sine for meditation
      oscillator.type = 'triangle'; // Softer than sine, less harsh
      oscillator.frequency.value = oscConfig.frequency;
      
      // Add low-pass filter to soften and warm the tone
      const lowpass = audioContext.createBiquadFilter();
      lowpass.type = 'lowpass';
      lowpass.frequency.value = 2000 + (index * 200); // Vary filter per oscillator
      lowpass.Q.value = 1; // Gentle rolloff
      
      // Apply amplitude with wave-like modulation
      const baseVolume = (oscConfig.amplitude || 0.8) * volume * 0.6; // Reduce overall volume for calmness
      gainNode.gain.value = 0;
      
      // Ocean wave-like fade in/out pattern
      // Each oscillator has a different wave phase for layered shifting
      const wavePeriod = 4 + (index * 0.3); // 4-8 second waves, offset per oscillator
      const wavePhase = (index * wavePeriod) / oscillators.length; // Stagger waves
      
      // Initial smooth fade in (adjust timing for transitions)
      gainNode.gain.setValueAtTime(0, currentTime);
      if (fadeIn) {
        // Smooth fade in for pattern transitions
        const transitionFadeTime = currentTime + fadeDuration;
        gainNode.gain.linearRampToValueAtTime(baseVolume * 0.5, transitionFadeTime);
      } else {
        // Original slow fade in
        gainNode.gain.linearRampToValueAtTime(baseVolume * 0.5, currentTime + 2); // Slow 2-second fade in
      }
      
      // Calculate when to start wave cycles based on fade type
      const startWaveTime = fadeIn 
        ? currentTime + fadeDuration + 0.5 + wavePhase 
        : currentTime + 2 + wavePhase;
      
      // Schedule multiple wave cycles (fade in/out like ocean waves)
      for (let cycle = 0; cycle < 50; cycle++) { // Schedule 50 cycles (~200-400 seconds)
        const cycleStart = startWaveTime + (cycle * wavePeriod);
        
        // Wave rising (fade in)
        gainNode.gain.setValueAtTime(baseVolume * 0.2, cycleStart);
        gainNode.gain.linearRampToValueAtTime(baseVolume * 0.5, cycleStart + wavePeriod * 0.3);
        gainNode.gain.linearRampToValueAtTime(baseVolume, cycleStart + wavePeriod * 0.5);
        
        // Wave falling (fade out)
        gainNode.gain.linearRampToValueAtTime(baseVolume * 0.5, cycleStart + wavePeriod * 0.7);
        gainNode.gain.linearRampToValueAtTime(baseVolume * 0.2, cycleStart + wavePeriod);
      }
      
      // Add gentle frequency drift (like ocean waves shifting)
      const driftLFO = audioContext.createOscillator();
      const driftGain = audioContext.createGain();
      driftLFO.type = 'sine';
      driftLFO.frequency.value = 0.05 + (index * 0.01); // Very slow drift (0.05-0.2 Hz)
      driftGain.gain.value = oscConfig.frequency * 0.02; // 2% frequency variation
      
      driftLFO.connect(driftGain);
      driftGain.connect(oscillator.frequency);
      driftLFO.start(currentTime);
      lfoOscillatorsRef.current.push(driftLFO);
      lfoGainNodesRef.current.push(driftGain);
      
      // Add LFO modulation if specified (for additional movement)
      if (oscConfig.lfoFrequency && oscConfig.lfoDepth) {
        const lfo = audioContext.createOscillator();
        const lfoGain = audioContext.createGain();
        
        lfo.type = 'sine';
        lfo.frequency.value = oscConfig.lfoFrequency * 0.5; // Slower for calmer effect
        lfoGain.gain.value = oscConfig.lfoDepth * oscConfig.frequency * 0.3; // Reduced depth
        
        // LFO modulates oscillator frequency gently
        lfo.connect(lfoGain);
        lfoGain.connect(oscillator.frequency);
        
        lfo.start(currentTime);
        lfoOscillatorsRef.current.push(lfo);
        lfoGainNodesRef.current.push(lfoGain);
      }
      
      // Connect: oscillator -> lowpass -> gain -> cymaticMasterGain
      oscillator.connect(lowpass);
      lowpass.connect(gainNode);
      gainNode.connect(cymaticMasterGain);
      
      oscillator.start(currentTime);
      
      oscillatorsRef.current.push(oscillator);
      gainNodesRef.current.push(gainNode);
    });
    
    setIsPlayingCymatic(true);
    
    // Reset transition flag after new sound starts
    if (fadeIn) {
      isTransitioningRef.current = false;
    }
    
    // Start audio analysis after a brief delay to ensure state is set
    setTimeout(() => {
      startAudioAnalysis();
    }, 100);
    
    const primaryFreq = getPrimaryFrequency(geometryPattern);
    const oscCount = oscillators.length;
    toast.success(`Playing complex ${oscCount}-oscillator cymatic matrix (${primaryFreq.toFixed(1)} Hz base)`);
  };

  /**
   * Toggle cymatic sound playback
   */
  const toggleCymaticSound = () => {
    if (isPlayingCymatic) {
      stopCymaticSound();
    } else {
      playCymaticSound();
    }
  };

  /**
   * Update volume for playing oscillators
   */
  useEffect(() => {
    if (isPlayingCymatic && cymaticMasterGainRef.current) {
      const currentTime = audioManager.getMainAudioContext()?.currentTime;
      if (typeof currentTime !== 'number') return;

      cymaticMasterGainRef.current.gain.cancelScheduledValues(currentTime);
      cymaticMasterGainRef.current.gain.setValueAtTime(
        cymaticMasterGainRef.current.gain.value,
        currentTime
      );
      cymaticMasterGainRef.current.gain.linearRampToValueAtTime(volume, currentTime + 0.05);
    }
  }, [volume, isPlayingCymatic, geometryPattern]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      stopCymaticSound();
    };
  }, []);

  /**
   * Handle smooth transition when pattern changes while playing
   */
  useEffect(() => {
    // Skip if we're already transitioning or if this is the initial mount
    if (isTransitioningRef.current) {
      return;
    }

    // If sound is playing and pattern changes, do smooth transition
    if (isPlayingCymatic) {
      isTransitioningRef.current = true;
      const fadeDuration = 0.5; // 500ms fade

      // Fade out current sound
      stopCymaticSound(true, fadeDuration);

      // After fade out completes, start new pattern with fade in
      setTimeout(async () => {
        await playCymaticSound(true, fadeDuration);
      }, fadeDuration * 1000 + 50); // Wait for fade out to complete
    }

    // Notify parent component of pattern change
    if (onPatternChange) {
      onPatternChange(geometryPattern);
    }
  }, [geometryPattern, onPatternChange]);

  return (
    <motion.div
      className="fr00t-card"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        background: 'rgba(30, 41, 59, 0.9)',
        borderColor: 'rgba(245, 158, 11, 0.3)'
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        marginBottom: '0.75rem'
      }}>
        <Waves size={24} color="#8b5cf6" />
        <h3 style={{ color: '#8b5cf6', fontSize: '1.25rem', fontWeight: '600', margin: 0, lineHeight: 1.2 }}>
          Cymatic Generator
        </h3>
      </div>

      {/* Sacred Geometry Canvas */}
      <div style={{ marginBottom: '0.75rem' }}>
        <h4 style={{
          color: '#8b5cf6',
          fontSize: '0.875rem',
          fontWeight: '600',
          margin: '0 0 0.5rem 0',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <Shapes size={18} />
          Sacred Geometry Pattern
        </h4>

        <SacredGeometryCanvas
          width={500}
          height={500}
          patternType={geometryPattern}
          color={PATTERN_THEMES[geometryPattern]?.borderColor || '#8b5cf6'}
          audioData={audioData}
          isPlaying={isPlayingCymatic}
        />

        {/* Pattern Selection */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(116px, 1fr))',
          gap: '0.58rem',
          marginTop: '0.75rem',
          marginBottom: '0.75rem'
        }}>
          {PATTERN_OPTIONS.map(pattern => {
            const active = geometryPattern === pattern.id;
            const isPlayingPattern = isPlayingCymatic && active;
            const theme = PATTERN_THEMES[pattern.id];
            return (
            <motion.button
              key={pattern.id}
              onClick={async () => {
                const newPattern = pattern.id;
                setGeometryPattern(newPattern);
                if (onPatternChange) {
                  onPatternChange(newPattern);
                }
                // If not playing, start playing with fade-in using the new pattern
                if (!isPlayingCymatic) {
                  await playCymaticSound(true, 0.5, newPattern);
                }
              }}
              whileHover={{ scale: 1.045, y: -2 }}
              whileTap={{ scale: 0.95 }}
              style={getPatternButtonStyle(pattern.id, active, isPlayingPattern)}
            >
              {isPlayingPattern && (
                <motion.span
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    inset: '-6px',
                    borderRadius: theme?.shape || '12px',
                    border: `2px solid ${(theme?.borderColor || '#8b5cf6')}aa`,
                    boxShadow: `0 0 18px ${(theme?.borderColor || '#8b5cf6')}bb`,
                    pointerEvents: 'none',
                    zIndex: 0
                  }}
                  animate={{ opacity: [0.45, 0.95, 0.45], scale: [1, 1.035, 1] }}
                  transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
                />
              )}
              <span style={{ fontSize: '0.9rem', lineHeight: 1, position: 'relative', zIndex: 1 }}>{theme?.glyph || '◉'}</span>
              <span style={{ lineHeight: 1.15, position: 'relative', zIndex: 1 }}>{pattern.name}</span>
              <span style={{ fontSize: '0.56rem', opacity: 0.9, letterSpacing: '0.06em', textTransform: 'uppercase', position: 'relative', zIndex: 1 }}>
                {theme?.archetype || pattern.category}
              </span>
            </motion.button>
            );
          })}
        </div>

        {/* Cymatic Sound Controls */}
        <div style={{
          marginTop: '0.75rem',
          padding: '1rem',
          background: 'rgba(139, 92, 246, 0.1)',
          borderRadius: '12px',
          border: '1px solid rgba(139, 92, 246, 0.3)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '0.75rem'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Volume2 size={18} color="#8b5cf6" />
              <h5 style={{
                color: '#8b5cf6',
                fontSize: '0.875rem',
                fontWeight: '600',
                margin: 0
              }}>
                Cymatic Sound Generator
              </h5>
            </div>
            <motion.button
              onClick={toggleCymaticSound}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                padding: '8px 16px',
                background: isPlayingCymatic ? 'rgba(239, 68, 68, 0.2)' : 'rgba(16, 185, 129, 0.2)',
                border: `2px solid ${isPlayingCymatic ? '#ef4444' : '#10b981'}`,
                borderRadius: '8px',
                color: isPlayingCymatic ? '#ef4444' : '#10b981',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.875rem'
              }}
            >
              {isPlayingCymatic ? (
                <>
                  <Square size={14} />
                  Stop
                </>
              ) : (
                <>
                  <Play size={14} />
                  Play
                </>
              )}
            </motion.button>
          </div>

          {/* Frequency Display */}
          {(() => {
            const freqConfig = getCymaticFrequency(geometryPattern);
            if (!freqConfig) return null;
            
            const primaryFreq = getPrimaryFrequency(geometryPattern);
            const oscCount = freqConfig.oscillators?.length || 0;
            const hasFM = !!(freqConfig.carrierFrequency && freqConfig.modulatorFrequency);
            
            return (
              <div style={{
                marginBottom: '0.75rem',
                padding: '0.75rem',
                background: 'rgba(15, 23, 42, 0.6)',
                borderRadius: '8px'
              }}>
                <div style={{
                  fontSize: '0.75rem',
                  color: '#64748b',
                  marginBottom: '0.25rem'
                }}>
                  Cymatic Frequency Matrix
                </div>
                <div style={{
                  fontSize: '1.25rem',
                  color: '#8b5cf6',
                  fontWeight: '600',
                  fontFamily: 'monospace',
                  marginBottom: '0.25rem'
                }}>
                  {primaryFreq.toFixed(1)} Hz
                </div>
                <div style={{
                  fontSize: '0.7rem',
                  color: '#10b981',
                  marginTop: '0.25rem',
                  fontWeight: '600'
                }}>
                  {oscCount} Independent Oscillators
                </div>
                {hasFM && (
                  <div style={{
                    fontSize: '0.7rem',
                    color: '#f59e0b',
                    marginTop: '0.25rem'
                  }}>
                    + FM Synthesis ({freqConfig.carrierFrequency?.toFixed(1)} Hz carrier)
                  </div>
                )}
                {freqConfig.oscillators?.some(osc => osc.lfoFrequency) && (
                  <div style={{
                    fontSize: '0.7rem',
                    color: '#6366f1',
                    marginTop: '0.25rem'
                  }}>
                    + LFO Modulation
                  </div>
                )}
                <div style={{
                  fontSize: '0.65rem',
                  color: '#64748b',
                  marginTop: '0.5rem',
                  fontStyle: 'italic',
                  lineHeight: '1.4'
                }}>
                  {getFrequencyDescription(geometryPattern)}
                </div>
                <div style={{
                  fontSize: '0.65rem',
                  color: '#8b5cf6',
                  marginTop: '0.5rem',
                  fontWeight: '600'
                }}>
                  Complexity: {freqConfig.complexity}/10
                </div>
              </div>
            );
          })()}

          {/* Volume Control */}
          <div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '0.5rem',
              fontSize: '0.75rem'
            }}>
              <label style={{ color: '#64748b' }}>Volume</label>
              <span style={{ color: '#8b5cf6', fontWeight: '600' }}>
                {Math.round(volume * 100)}%
              </span>
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              style={{
                width: '100%',
                height: '6px',
                borderRadius: '3px',
                outline: 'none',
                background: `linear-gradient(to right, #8b5cf6 0%, #8b5cf6 ${volume * 100}%, rgba(100, 116, 139, 0.3) ${volume * 100}%, rgba(100, 116, 139, 0.3) 100%)`
              }}
            />
          </div>
        </div>
      </div>

    </motion.div>
  );
};

export default VisualHarmonyStudio;
